import React from 'react';
import Alert from '@/components/UI/Alert';
import Button from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import { FormNumberInput, FormSelect } from '@/components/UI';
import type { BrokerCapitalBasisSummary, PortfolioCapitalBasis } from '@einhaus/project314-types';
import type {
    AllocationDirection,
    CapitalBasisOption,
    LinkedTradingAccountRow,
    PortfolioBalanceSummary,
    PortfolioCapitalAllocationDisplayRow,
    PortfolioWithAccountingFields
} from './types';
import { capitalBasisLabels } from './constants';
import { formatCurrency } from './helpers';

interface AllocateFundsModalProps {
    isOpen: boolean;
    portfolio: PortfolioWithAccountingFields;
    balanceSummary: PortfolioBalanceSummary;
    usesBrokerCapitalAllocation: boolean;
    linkedTradingAccount: LinkedTradingAccountRow | null;
    availableCapitalBasisOptions: CapitalBasisOption[];
    selectedAllocationCapitalBasis: PortfolioCapitalBasis;
    selectedCapitalBasisSummary: BrokerCapitalBasisSummary | null;
    selectedCapitalBasisAllocatedAmount: number;
    unavailableCapitalBasisRows: PortfolioCapitalAllocationDisplayRow[];
    allocationAmount: number | null;
    allocationDirection: AllocationDirection;
    isLoading: boolean;
    error: string | null;
    onAmountChange: (amount: number | null) => void;
    onDirectionChange: (direction: AllocationDirection) => void;
    onCapitalBasisChange: (capitalBasis: PortfolioCapitalBasis) => void;
    onAllocateFunds: () => void;
    onClose: () => void;
}

const AllocateFundsModal: React.FC<AllocateFundsModalProps> = ({
    isOpen,
    portfolio,
    balanceSummary,
    usesBrokerCapitalAllocation,
    linkedTradingAccount,
    availableCapitalBasisOptions,
    selectedAllocationCapitalBasis,
    selectedCapitalBasisSummary,
    selectedCapitalBasisAllocatedAmount,
    unavailableCapitalBasisRows,
    allocationAmount,
    allocationDirection,
    isLoading,
    error,
    onAmountChange,
    onDirectionChange,
    onCapitalBasisChange,
    onAllocateFunds,
    onClose
}) => {
    return (
        <Modal isOpen={isOpen} size="md" title="Allocate Funds" onClose={onClose}>
            <div className="p-3">
                {error && <Alert variant="danger">{error}</Alert>}

                <div className="mb-4">
                    <h5 className="text-white font-medium mb-2">Portfolio Capital</h5>
                    {portfolio && (
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse bg-[#1a1a1a] border border-zinc-700">
                                <tbody>
                                    <tr>
                                        <td className="border border-zinc-700 p-2">Cash Ledger:</td>
                                        <td className="border border-zinc-700 p-2">{formatCurrency(portfolio.cashBalance)}</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-zinc-700 p-2">Current Equity:</td>
                                        <td className="border border-zinc-700 p-2">{formatCurrency(balanceSummary.currentEquity)}</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-zinc-700 p-2">Reporting Capital Base:</td>
                                        <td className="border border-zinc-700 p-2">
                                            {formatCurrency(balanceSummary.reportingCapitalBase)}
                                        </td>
                                    </tr>
                                    {usesBrokerCapitalAllocation && selectedCapitalBasisSummary && (
                                        <>
                                            <tr>
                                                <td className="border border-zinc-700 p-2">Linked Account:</td>
                                                <td className="border border-zinc-700 p-2">{linkedTradingAccount?.name}</td>
                                            </tr>
                                            <tr>
                                                <td className="border border-zinc-700 p-2">
                                                    Portfolio Allocation ({capitalBasisLabels[selectedAllocationCapitalBasis]}):
                                                </td>
                                                <td className="border border-zinc-700 p-2">
                                                    {formatCurrency(selectedCapitalBasisAllocatedAmount)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="border border-zinc-700 p-2">
                                                    Account Capacity ({capitalBasisLabels[selectedAllocationCapitalBasis]}):
                                                </td>
                                                <td className="border border-zinc-700 p-2">
                                                    {formatCurrency(selectedCapitalBasisSummary.totalCapacity)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="border border-zinc-700 p-2">Available To Allocate:</td>
                                                <td className="border border-zinc-700 p-2">
                                                    {formatCurrency(selectedCapitalBasisSummary.availableToAllocate)}
                                                </td>
                                            </tr>
                                            {selectedCapitalBasisSummary.overAllocatedAmount > 0 && (
                                                <tr>
                                                    <td className="border border-zinc-700 p-2 text-yellow-300">Over Capacity:</td>
                                                    <td className="border border-zinc-700 p-2 text-yellow-300">
                                                        {formatCurrency(selectedCapitalBasisSummary.overAllocatedAmount)}
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="allocationDirection">
                        Transaction Type
                    </label>
                    <FormSelect
                        id="allocationDirection"
                        options={[
                            { value: 'deposit', label: usesBrokerCapitalAllocation ? 'Allocate' : 'Deposit' },
                            { value: 'withdraw', label: usesBrokerCapitalAllocation ? 'Deallocate' : 'Withdraw' }
                        ]}
                        value={allocationDirection}
                        onChange={(e) => onDirectionChange(e.target.value as AllocationDirection)}
                    />
                </div>

                {usesBrokerCapitalAllocation && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="allocationCapitalBasis">
                            Capital Basis
                        </label>
                        <FormSelect
                            id="allocationCapitalBasis"
                            options={availableCapitalBasisOptions}
                            value={selectedAllocationCapitalBasis}
                            onChange={(e) => onCapitalBasisChange(e.target.value as PortfolioCapitalBasis)}
                        />
                        <div className="mt-2 text-sm text-zinc-400">
                            Non-cash allocations reserve broker-backed capacity for option strategies without changing the portfolio cash
                            balance.
                        </div>
                        {unavailableCapitalBasisRows.length > 0 && (
                            <div className="mt-2 text-sm text-yellow-300">
                                {unavailableCapitalBasisRows.map((summary) => (
                                    <div key={`modal-warning-${summary.capitalBasis}`}>
                                        {summary.label}: {summary.supportReason}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="mb-4">
                    <FormNumberInput
                        noMargin
                        id="allocationAmount"
                        label="Amount"
                        min={0}
                        placeholder="0.00"
                        prefix="$"
                        step={0.01}
                        value={allocationAmount}
                        onValueChange={onAmountChange}
                    />
                    {allocationDirection === 'withdraw' &&
                        portfolio &&
                        allocationAmount !== null &&
                        allocationAmount >
                            (usesBrokerCapitalAllocation && linkedTradingAccount
                                ? selectedCapitalBasisAllocatedAmount
                                : portfolio.cashBalance) && (
                            <div className="text-red-500 mt-1">
                                {usesBrokerCapitalAllocation
                                    ? 'Amount exceeds the portfolio allocation for the selected capital basis'
                                    : 'Amount exceeds available cash balance'}
                            </div>
                        )}
                    {usesBrokerCapitalAllocation && selectedCapitalBasisSummary?.supportReason && (
                        <div className="text-yellow-400 mt-1">{selectedCapitalBasisSummary.supportReason}</div>
                    )}
                </div>

                <div className="flex justify-end">
                    <Button className="mr-2" disabled={isLoading} variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        disabled={
                            isLoading ||
                            allocationAmount === null ||
                            allocationAmount <= 0 ||
                            (allocationDirection === 'withdraw' &&
                                portfolio &&
                                allocationAmount >
                                    (usesBrokerCapitalAllocation && linkedTradingAccount
                                        ? selectedCapitalBasisAllocatedAmount
                                        : portfolio.cashBalance))
                        }
                        variant="primary"
                        onClick={onAllocateFunds}
                    >
                        {isLoading
                            ? 'Processing...'
                            : allocationDirection === 'deposit'
                              ? usesBrokerCapitalAllocation
                                  ? 'Allocate'
                                  : 'Deposit'
                              : usesBrokerCapitalAllocation
                                ? 'Deallocate'
                                : 'Withdraw'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AllocateFundsModal;
